// Recovery from stale webpack chunks (ChunkLoadError).
//
// The site ships content-hashed chunks from an immutable container. On deploy,
// the served files are replaced wholesale, so a browser holding an old page
// requests a hashed chunk that no longer exists -> nginx 404 -> webpack throws
// a ChunkLoadError. Reloading fetches fresh HTML with the current hashes.
//
// A one-time guard is mandatory: a genuinely broken chunk would otherwise loop
// forever and hammer the origin. See Gatsby's own warning about reload loops.

const RELOAD_FLAG = 'cvo-chunk-reload';

// Suppress a repeat reload within this window so a genuinely broken deploy (the
// fresh page fails too) cannot loop. After it elapses, a later deploy's stale
// chunk is free to trigger a fresh recovery reload — the guard self-heals.
const RELOAD_COOLDOWN_MS = 10000;

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk \S+ failed/i,
  /Loading CSS chunk \S+ failed/i,
  /ChunkLoadError/i,
  /Importing a module script failed/i,
  // A 404 that returns the HTML fallback is parsed as a script and rejected.
  /'text\/html' is not a valid JavaScript MIME type/i,
];

/**
 * Detect a stale-chunk failure from a thrown error or an ErrorEvent payload.
 * @param {unknown} error
 * @returns {boolean}
 */
export const isChunkLoadError = (error) => {
  if (!error) {
    return false;
  }
  if (error.name === 'ChunkLoadError') {
    return true;
  }
  const message = typeof error === 'string' ? error : error.message;
  if (!message) {
    return false;
  }
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

/**
 * Reload the page, at most once per cooldown window. A timestamp guard bounds
 * recovery to one reload regardless of when the failure fires (initial load or a
 * later navigation), so a truly broken deploy surfaces the error instead of
 * triggering an infinite reload loop.
 */
export const reloadOnce = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    const lastReload = Number(window.sessionStorage.getItem(RELOAD_FLAG));
    if (lastReload && Date.now() - lastReload < RELOAD_COOLDOWN_MS) {
      return;
    }
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // Private mode / storage disabled: skip the guard rather than crash.
    return;
  }
  window.location.reload();
};

/**
 * Register a global error listener that reloads once on a chunk-load failure.
 * Catches 404 script-load errors that never reach React's error boundaries.
 */
export const registerChunkReloadListener = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event?.error) || isChunkLoadError(event?.message)) {
      reloadOnce();
    }
  });
};
